/**
 * X3DH + Double Ratchet (ADR-016).
 *
 * Pure logic: no storage, no network, no clock. State goes in and out as a
 * plain object so the layer above can persist it and so the Swift and Kotlin
 * ports can be held to the same vectors.
 *
 * What this buys over a long-lived conversation key: message keys are
 * destroyed after use, so a key stolen today does not open yesterday; and a
 * DH step re-keys the session, so an attacker who loses access is locked back
 * out. Neither is possible with a key that stays the same.
 *
 * What it costs, stated once: a new device starts with an empty chat. That is
 * forward secrecy working, not a defect.
 */
import nacl from 'tweetnacl';
import { hmacSha512, hkdf } from './hkdf.mjs';

const enc = new TextEncoder();

export const X3DH_INFO   = 'VoxioxX3DHv1';
export const RATCHET_INFO = 'VoxioxRatchetV1';
export const MSG_INFO    = 'VoxioxMsgV1';
export const PREKEY_SIG_CONTEXT = 'VoxioxPreKeyV1';

/**
 * How long a skipped message key may be kept, and how many.
 *
 * ADR-016 promises seven days; this is where that promise is kept. The count
 * is a second bound for a week busy enough to make the first one useless.
 */
export const SKIPPED_TTL_MS = 7 * 24 * 60 * 60 * 1000;
export const MAX_SKIPPED_KEYS = 2000;

/** Injectable so tests do not have to wait a week. */
let now = () => Date.now();
export function _setClock(fn) { now = fn || (() => Date.now()); }

/** A header claiming a million skipped messages must cost nothing to refuse. */
export const MAX_SKIP = 1000;
export const HEADER_BYTES = 1 + 32 + 4 + 4;   // version, DH pub, PN, N

function concat(...parts) {
  const total = parts.reduce((n, p) => n + p.length, 0);
  const out = new Uint8Array(total);
  let off = 0;
  for (const p of parts) { out.set(p, off); off += p.length; }
  return out;
}
function u32be(n) {
  return new Uint8Array([(n >>> 24) & 0xff, (n >>> 16) & 0xff, (n >>> 8) & 0xff, n & 0xff]);
}
function readU32be(b, off) {
  return ((b[off] << 24) | (b[off + 1] << 16) | (b[off + 2] << 8) | b[off + 3]) >>> 0;
}
function b64(u8) {
  let s = ''; for (let i = 0; i < u8.length; i++) s += String.fromCharCode(u8[i]);
  return btoa(s);
}
function unb64(s) {
  const bin = atob(s); const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}
function equal(a, b) {
  if (!a || !b || a.length !== b.length) return false;
  let d = 0; for (let i = 0; i < a.length; i++) d |= a[i] ^ b[i];
  return d === 0;
}

/**
 * Raw X25519 — NOT nacl.box.before.
 *
 * box.before is HSalsa20(scalarMult(...)), and every existing native module in
 * this project uses beforenm for the audio tunnel. Reaching for the familiar
 * one here gives a session that establishes and then decrypts nothing, on one
 * platform only. ADR-011 §10 named this trap in advance; it is spelled out
 * here so a port does not walk into it.
 */
function dh(secretKey, publicKey) {
  return nacl.scalarMult(secretKey, publicKey);
}

// ─── Prekeys ────────────────────────────────────────────────────────────────

export function generatePreKey() { return nacl.box.keyPair(); }

export function signPreKey(identitySigSecret, preKeyPub) {
  return nacl.sign.detached(concat(enc.encode(PREKEY_SIG_CONTEXT), preKeyPub), identitySigSecret);
}

export function verifyPreKey(sig, preKeyPub, identitySigPub) {
  try {
    return nacl.sign.detached.verify(
      concat(enc.encode(PREKEY_SIG_CONTEXT), preKeyPub), sig, identitySigPub);
  } catch (e) { return false; }
}

// ─── X3DH ───────────────────────────────────────────────────────────────────

/** Associated data for the whole session: both identity DH keys, initiator first. */
export function associatedData(initiatorIdDhPub, responderIdDhPub) {
  return concat(initiatorIdDhPub, responderIdDhPub);
}

/**
 * Alice's side. `theirBundle` is what the server served; its signature must
 * already have been checked against a TOFU-pinned identity key (ADR-014) —
 * this function does not know who is trusted, only how to do the arithmetic.
 */
export function x3dhInitiate({ myIdentityDh, theirIdentityDhPub, theirSignedPreKeyPub, theirOneTimePreKeyPub = null }) {
  const ek = nacl.box.keyPair();
  const parts = [
    dh(myIdentityDh.secretKey, theirSignedPreKeyPub),   // DH1: my identity  ↔ their signed prekey
    dh(ek.secretKey, theirIdentityDhPub),               // DH2: my ephemeral ↔ their identity
    dh(ek.secretKey, theirSignedPreKeyPub),             // DH3: my ephemeral ↔ their signed prekey
  ];
  if (theirOneTimePreKeyPub) parts.push(dh(ek.secretKey, theirOneTimePreKeyPub));
  const sk = nacl.hash(concat(enc.encode(X3DH_INFO), ...parts)).slice(0, 32);
  return {
    sk,
    ephemeralPub: ek.publicKey,
    ad: associatedData(myIdentityDh.publicKey, theirIdentityDhPub),
  };
}

/** Bob's side, from the initial message. The same four terms, other way round. */
export function x3dhRespond({ myIdentityDh, mySignedPreKey, myOneTimePreKey = null, theirIdentityDhPub, theirEphemeralPub }) {
  const parts = [
    dh(mySignedPreKey.secretKey, theirIdentityDhPub),
    dh(myIdentityDh.secretKey, theirEphemeralPub),
    dh(mySignedPreKey.secretKey, theirEphemeralPub),
  ];
  if (myOneTimePreKey) parts.push(dh(myOneTimePreKey.secretKey, theirEphemeralPub));
  const sk = nacl.hash(concat(enc.encode(X3DH_INFO), ...parts)).slice(0, 32);
  return { sk, ad: associatedData(theirIdentityDhPub, myIdentityDh.publicKey) };
}

// ─── Ratchet KDFs ───────────────────────────────────────────────────────────

function kdfRootKey(rk, dhOut) {
  const out = hkdf(dhOut, rk, enc.encode(RATCHET_INFO), 64);
  return { rk: out.slice(0, 32), ck: out.slice(32, 64) };
}

// Two distinct constants so the chain key and the message key can never be the
// same value — the message key is given away to the decryptor's memory, the
// chain key must not follow it.
function kdfChainKey(ck) {
  return {
    ck: hmacSha512(ck, new Uint8Array([0x02])).slice(0, 32),
    mk: hmacSha512(ck, new Uint8Array([0x01])).slice(0, 32),
  };
}

// ─── Session ────────────────────────────────────────────────────────────────

/** Alice: she knows Bob's signed prekey and starts the sending chain at once. */
export function initSender(sk, theirSignedPreKeyPub, ad) {
  const dhs = nacl.box.keyPair();
  const { rk, ck } = kdfRootKey(sk, dh(dhs.secretKey, theirSignedPreKeyPub));
  return { rk, cks: ck, ckr: null, dhs, dhr: theirSignedPreKeyPub, ns: 0, nr: 0, pn: 0, skipped: new Map(), ad };
}

/** Bob: his ratchet key IS the signed prekey until Alice's first message. */
export function initReceiver(sk, mySignedPreKey, ad) {
  return { rk: sk, cks: null, ckr: null, dhs: mySignedPreKey, dhr: null, ns: 0, nr: 0, pn: 0, skipped: new Map(), ad };
}

function header(state) {
  return concat(new Uint8Array([1]), state.dhs.publicKey, u32be(state.pn), u32be(state.ns));
}

export function parseHeader(frame) {
  if (!frame || frame.length < HEADER_BYTES + 16 || frame[0] !== 1) return null;
  return {
    dhPub: frame.slice(1, 33),
    pn: readU32be(frame, 33),
    n: readU32be(frame, 37),
    body: frame.slice(HEADER_BYTES),
  };
}

// The message key is stretched into a key and a nonce over the header, so a
// tampered header simply fails to open. secretbox has no place for associated
// data; this buys the same property.
function messageKeyMaterial(mk, ad, hdr) {
  const out = hkdf(mk, new Uint8Array(0), concat(enc.encode(MSG_INFO), ad, hdr), 56);
  return { key: out.slice(0, 32), nonce: out.slice(32, 56) };
}

export function ratchetEncrypt(state, plaintext) {
  if (!state.cks) throw new Error('ratchetEncrypt: no sending chain — this side must receive first');
  const { ck, mk } = kdfChainKey(state.cks);
  const hdr = header(state);
  state.cks = ck;
  state.ns += 1;
  const { key, nonce } = messageKeyMaterial(mk, state.ad, hdr);
  return concat(hdr, nacl.secretbox(plaintext, nonce, key));
}

function trySkipped(state, hdr, h) {
  const id = `${b64(h.dhPub)}:${h.n}`;
  const held = state.skipped.get(id);
  if (!held) return null;
  const mk = held.mk;
  const { key, nonce } = messageKeyMaterial(mk, state.ad, hdr);
  const out = nacl.secretbox.open(h.body, nonce, key);
  // Used once, then gone: a stored key that survives its message is a stored
  // way to read that message again.
  if (out) state.skipped.delete(id);
  return out;
}

function skipTo(state, until) {
  if (!state.ckr) return;
  if (until - state.nr > MAX_SKIP) {
    throw new Error(`ratchetDecrypt: header claims ${until - state.nr} skipped messages, refusing`);
  }
  while (state.nr < until) {
    const { ck, mk } = kdfChainKey(state.ckr);
    // Stored WITH the moment it was derived. A skipped key is a stored way to
    // read one old message, and ADR-016 promises it does not outlive a week —
    // a promise nothing enforced: there were no timestamps, no cap on the map
    // and no pruning, so a device carried every key it had ever skipped, and
    // whoever took the device could open messages that forward secrecy said
    // were already unreadable.
    state.skipped.set(`${b64(state.dhr)}:${state.nr}`, { mk, at: now() });
    state.ckr = ck;
    state.nr += 1;
  }
  pruneSkipped(state);
}

/**
 * Drop skipped keys that are too old, and the oldest ones when there are too
 * many. Both bounds matter: age is the promise, count is the backstop for a
 * chatty week.
 */
export function pruneSkipped(state, at = now()) {
  if (!state.skipped || state.skipped.size === 0) return state;
  for (const [id, held] of state.skipped) {
    if (!held || typeof held.at !== 'number' || at - held.at > SKIPPED_TTL_MS) {
      state.skipped.delete(id);
    }
  }
  if (state.skipped.size > MAX_SKIPPED_KEYS) {
    const byAge = [...state.skipped.entries()].sort((a, b) => a[1].at - b[1].at);
    for (const [id] of byAge.slice(0, state.skipped.size - MAX_SKIPPED_KEYS)) {
      state.skipped.delete(id);
    }
  }
  return state;
}

function dhRatchet(state, h) {
  state.pn = state.ns;
  state.ns = 0;
  state.nr = 0;
  state.dhr = h.dhPub;
  ({ rk: state.rk, ck: state.ckr } = kdfRootKey(state.rk, dh(state.dhs.secretKey, state.dhr)));
  state.dhs = nacl.box.keyPair();
  ({ rk: state.rk, ck: state.cks } = kdfRootKey(state.rk, dh(state.dhs.secretKey, state.dhr)));
}

/**
 * Returns the plaintext, or null. Null covers every failure — wrong key,
 * tampered header, a message whose key is long gone — deliberately: a caller
 * that cannot tell them apart cannot leak which it was.
 */
/**
 * `diag`, when given, is filled with why this failed. A ratchet that says only
 * "no" is undiagnosable from a log: a message that will never open because the
 * chains diverged and one that arrived out of order look identical on screen,
 * and the difference decides whether the session must be rebuilt or waited on.
 */
export function ratchetDecrypt(state, frame, diag = null) {
  const say = (why, extra) => { if (diag) Object.assign(diag, { why }, extra || {}); };
  const h = parseHeader(frame);
  if (!h) { say('header', { len: frame ? frame.length : 0, v: frame && frame.length ? frame[0] : null }); return null; }
  const hdr = frame.slice(0, HEADER_BYTES);
  say('?', {
    n: h.n, pn: h.pn, ourNr: state.nr, ourNs: state.ns,
    sameDh: !!(state.dhr && equal(state.dhr, h.dhPub)),
    haveRecvChain: !!state.ckr, skippedHeld: state.skipped ? state.skipped.size : 0,
  });

  const fromSkipped = trySkipped(state, hdr, h);
  if (fromSkipped) { say('ok-skipped'); return fromSkipped; }

  // A ratchet step is irreversible, so nothing is committed until the message
  // it came with actually opens. Work on a copy and swap on success.
  const work = cloneState(state);
  try {
    if (!equal(work.dhr, h.dhPub)) {
      skipTo(work, h.pn);
      dhRatchet(work, h);
    }
    skipTo(work, h.n);
    if (!work.ckr) { say('no-recv-chain'); return null; }
    const { ck, mk } = kdfChainKey(work.ckr);
    const { key, nonce } = messageKeyMaterial(mk, work.ad, hdr);
    const out = nacl.secretbox.open(h.body, nonce, key);
    if (!out) { say('mac'); return null; }
    work.ckr = ck;
    work.nr += 1;
    adoptState(state, work);
    say('ok');
    return out;
  } catch (e) {
    say('threw', { error: e.message });
    return null;
  }
}

// ─── Persistence ────────────────────────────────────────────────────────────

function cloneState(s) {
  return {
    rk: s.rk && s.rk.slice(), cks: s.cks && s.cks.slice(), ckr: s.ckr && s.ckr.slice(),
    dhs: { publicKey: s.dhs.publicKey.slice(), secretKey: s.dhs.secretKey.slice() },
    dhr: s.dhr && s.dhr.slice(),
    ns: s.ns, nr: s.nr, pn: s.pn,
    skipped: new Map(s.skipped), ad: s.ad.slice(),
  };
}
function adoptState(target, src) { Object.assign(target, src); }

export function serializeState(s) {
  return {
    v: 2,
    rk: s.rk ? b64(s.rk) : null,
    cks: s.cks ? b64(s.cks) : null,
    ckr: s.ckr ? b64(s.ckr) : null,
    dhs: { publicKey: b64(s.dhs.publicKey), secretKey: b64(s.dhs.secretKey) },
    dhr: s.dhr ? b64(s.dhr) : null,
    ns: s.ns, nr: s.nr, pn: s.pn,
    ad: b64(s.ad),
    // v2 carries the age of each key. A v1 blob has none, and is read back
    // below as "seen just now" — one grace week from the upgrade, rather than
    // deleting history somebody may still be reading.
    skipped: [...s.skipped].map(([k, v]) => [k, b64(v.mk), v.at]),
  };
}

export function deserializeState(j) {
  // v1 had no timestamps on skipped keys. It is read, not refused: a device
  // upgrading mid-conversation must not lose its session. Those keys are
  // stamped with the moment they were read back, so they get one grace week
  // from the upgrade instead of living for ever.
  if (!j || (j.v !== 1 && j.v !== 2)) throw new Error('deserializeState: unknown state version');
  const at = now();
  const state = {
    rk: j.rk ? unb64(j.rk) : null,
    cks: j.cks ? unb64(j.cks) : null,
    ckr: j.ckr ? unb64(j.ckr) : null,
    dhs: { publicKey: unb64(j.dhs.publicKey), secretKey: unb64(j.dhs.secretKey) },
    dhr: j.dhr ? unb64(j.dhr) : null,
    ns: j.ns, nr: j.nr, pn: j.pn,
    ad: unb64(j.ad),
    skipped: new Map((j.skipped || []).map(([k, v, ts]) => [k, {
      mk: unb64(v), at: typeof ts === 'number' ? ts : at,
    }])),
  };
  // Expiry is enforced on the way IN as well as on the way out: a session that
  // sat on disk for a month must not come back with a month of keys.
  return pruneSkipped(state, at);
}
